import { useSuspenseQuery } from '@apollo/client';
import type React from 'react';
import { Suspense } from 'react';
import { useIntl } from 'react-intl';

import { ErrorBoundary } from '@xing-com/crate-core-error-boundary';
import { useParams } from '@xing-com/crate-core-router';
import { PageLayout } from '@xing-com/crate-people-directory-page-layout';
import { UserBags } from '@xing-com/crate-people-directory-user-bags';
import { UserChunk } from '@xing-com/crate-people-directory-user-chunk';
import {
  UserMessage,
  UserMessageType,
} from '@xing-com/crate-people-directory-user-message';
import { useMediaListener, BREAKPOINTS } from '@xing-com/util';
import { Column, Grid, Row } from '@xing-com/xing-grid';

import { SPECIAL_CHARACTER_ID, SEO_URL_META_DATA } from './constants';
import { LetterPageByCityDocument } from './graphql/letter-page-by-city.query.gql-types';
import { sanitizePopularNames, sanitizeNames } from './helpers';
import { ContentRow, StyledHeadline } from './letter-page-by-city.styles';

type BasicPageLayoutProps = {
  seoTitle: string;
  seoDescription: string;
  index: boolean;
  letter: string;
  seoUrl: string;
  pageName: string;
};

const LetterPageByCityInner: React.FC<BasicPageLayoutProps> = (
  basicPageLayoutProps
) => {
  const { country, city: citySlug, citySlugOrLetter: letter } = useParams();
  const { data, error } = useSuspenseQuery(LetterPageByCityDocument, {
    variables: { letter, city: citySlug, country },
    skip: !letter || !citySlug || !country,
  });

  if (error || !data) throw error;

  const { formatMessage } = useIntl();
  const breakpoint = useMediaListener();
  const isDesktop = breakpoint === BREAKPOINTS.desktop;

  const { names, popularNames, name, toName, city, breadcrumbs } =
    data?.peopleByCityTopLevelPage || {};

  const sanitizedBreadcrumbs = breadcrumbs?.map((breadcrumb) => {
    return {
      title: breadcrumb?.title || '',
      url: `${breadcrumb?.url}` || '',
    };
  });

  if (!data?.peopleByCityTopLevelPage || !names || !names.length) {
    return <EmptyStateFallback {...basicPageLayoutProps} />;
  }

  const headlineMessage = formatMessage(
    {
      id: 'PD_CITIES_LAST_NAMES_IN_CITY_HEADLINE',
    },
    { letter: letter.toUpperCase(), city }
  );
  const isSpecialCharacter = letter === SPECIAL_CHARACTER_ID;
  const seoTitle = formatMessage(
    {
      id: 'PD_CITIES_CITY_LETTER_PAGE_TITLE',
    },
    {
      title: formatMessage(
        {
          id: isSpecialCharacter
            ? 'PD_200_LASTNAMES_SPECIAL'
            : 'PD_200_LASTNAMES_STARTING',
        },
        { letter_variable: letter.toUpperCase() }
      ),
      city,
    }
  );

  const seoDescription = formatMessage(
    { id: 'PD_CITY_COLLECTION_META_DESCRIPTION' },
    { display_name: `${name} … ${toName}`, city }
  );

  const pageLayoutProps = {
    ...basicPageLayoutProps,
    seoTitle,
    seoDescription,
    breadcrumbs: sanitizedBreadcrumbs,
  };

  const withUserBags = popularNames && popularNames.length;

  return (
    <PageLayout {...pageLayoutProps}>
      <Grid>
        {isDesktop ? (
          <Row>
            <Column sizeWide={12} size={0}>
              <StyledHeadline size="xxlarge">{headlineMessage}</StyledHeadline>
            </Column>
          </Row>
        ) : null}
        <ContentRow $isLoading={false}>
          <Column size={12} sizeWide={withUserBags ? 8 : 12}>
            {!isDesktop ? (
              <StyledHeadline size="large">{headlineMessage}</StyledHeadline>
            ) : null}
            {names ? (
              <UserChunk
                names={sanitizeNames(names)}
                fullWidthLayout={!withUserBags}
              />
            ) : null}
          </Column>
          {withUserBags ? (
            <Column size={12} sizeWide={4}>
              <UserBags
                activeLetter={letter}
                popularNames={sanitizePopularNames(popularNames)}
              />
            </Column>
          ) : null}
        </ContentRow>
      </Grid>
    </PageLayout>
  );
};

const LoadingStateFallback: React.FC<BasicPageLayoutProps> = (
  basicPageLayoutProps
) => {
  return (
    <PageLayout {...basicPageLayoutProps}>
      <Grid>
        <ContentRow $isLoading={true}>
          <Column size={12}>
            <StyledHeadline size="large">...</StyledHeadline>
          </Column>
        </ContentRow>
      </Grid>
    </PageLayout>
  );
};

const EmptyStateFallback: React.FC<BasicPageLayoutProps> = (
  basicPageLayoutProps
) => {
  return (
    <PageLayout {...basicPageLayoutProps}>
      <UserMessage type={UserMessageType.empty} />
    </PageLayout>
  );
};

const ErrorFallback: React.FC<BasicPageLayoutProps> = (
  basicPageLayoutProps
) => {
  return (
    <PageLayout {...basicPageLayoutProps}>
      <UserMessage type={UserMessageType.error} />
    </PageLayout>
  );
};

export const LetterPageByCity: React.FC = () => {
  const { formatMessage } = useIntl();
  const { country, city: citySlug, citySlugOrLetter: letter } = useParams();
  const basicPageLayoutProps = {
    letter,
    pageHeaderAsSecondaryHeadline: true,
    seoTitle: formatMessage({ id: 'PD_100_HEADING' }),
    seoDescription: formatMessage({ id: 'PD_100_DESCR' }),
    index: true,
    seoUrl: `${SEO_URL_META_DATA}/cities/${country}/${citySlug}/${letter}`,
    pageName: `cities/${country}/${letter}`,
  };

  return (
    <ErrorBoundary onError={() => <ErrorFallback {...basicPageLayoutProps} />}>
      <Suspense fallback={<LoadingStateFallback {...basicPageLayoutProps} />}>
        <LetterPageByCityInner {...basicPageLayoutProps} />
      </Suspense>
    </ErrorBoundary>
  );
};
